import {
  Avatar,
  Grid,
  Box,
  Typography,
  Container,
  Alert,
  Link,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { Form, useLocation, useNavigate } from "react-router-dom";
import intl from "react-intl-universal";
import Copyright from "../components/Copyright";
import { MuiOtpInput } from "mui-one-time-password-input";
import { useState } from "react";
import OtpService from "../services/OtpService";
import SecurityIcon from '@mui/icons-material/Security';

export default function SendOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [otpMethod, setOtpMethod] = useState("email"); // New state for selecting OTP method


  function getCode() {
    setOtpSent(true);
    sendOtp();
  }


  async function sendOtp() {
    setErrorMessage('');
    const contactInfo = otpMethod === "email" ? state?.email : state?.phone;
    if (!contactInfo) {
      console.error("Invalid contact information");
      setErrorMessage(intl.get("invalidcontactinfo").d("Invalid contact information"));
      return;
    }

    try {
      const response = await OtpService.send(state.companyName, otpMethod, contactInfo);
      if (response?.ok) {
        setOtpSent(true);
        console.log("OTP sent successfully");
      } else {
        setOtpSent(false);
        setErrorMessage( (response?.error? response.error + ".  " : "") +  intl.get("sendotperror").d("An error occurred while sending the OTP. Please try again or contact support."));
      }
    } catch (error) {
      setOtpSent(false);
      console.error("Error sending OTP", error);
      setErrorMessage(intl.get("sendotperror").d("An error occurred while sending the OTP. Please try again or contact support."));
    }
  }


  async function resendOtp(event) {
    event.preventDefault();
    await sendOtp();
  }


  async function validateOtp(otp) {
    const contactInfo = otpMethod === "email" ? state?.email : state?.phone;
    if (!contactInfo) {
      console.error("Invalid contact information");
      setErrorMessage(intl.get("invalidcontactinfo").d("Invalid contact information"));
      return;
    }

    if (!otp || otp.length < 6) {
      setErrorMessage(intl.get("invalidcode").d("Invalid code. Please try again."));
      return;
    }

    try {
      const response = await OtpService.validate(contactInfo, otp);
      if (response && response.isValid) {
        setErrorMessage("");
        
        console.debug( "Otp is valid.");

        // navigate to nextPath if set in the state
        if( state.nextPath ){
            navigate( state.nextPath, {state} );
		}

      } else {
        setErrorMessage(intl.get("invalidcode").d("Invalid code. Please try again."));
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(intl.get("validationerror").d("An error occurred during validation. Please try again."));
    }
  }
  

  function handleOtpChange(otp) {
    setOtp(otp);
    setErrorMessage(""); // Clear error message when OTP is changed
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
          <SecurityIcon />
        </Avatar>

        <Typography component="h1" variant="h5">
          {intl.get("twofactorauthentication").d("Two-Factor Authentication")}
        </Typography>

        <Box sx={{ mt: 1 }}>
          <Form method="post" sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} container justifyContent="center">
                <Typography component="p" variant="caption">
                  {otpSent
                    ? intl.get("tocontinuepleaseenter").d("To continue, please enter the six-digit verification code sent to")
                    : intl.get("averificationcodewillbesentto").d("A verification code will be sent to")}
                  &nbsp;
                  <b>{otpMethod === "email" ? state?.email : state?.phone || intl.get("invalidcontactinfo").d("Invalid contact information")}</b>.
                  {otpSent && <br />}
                  
                  {otpSent && intl.get("thiscodeisvalidforthenext10minutes").d("This code is valid for the next 10 minutes.") }
                  {otpSent && <br />}
                  {otpSent && otpMethod === "email" ? intl.get("checkyourspamfolder").d("If you haven't received the code, please check your spam or junk folder. If it's not there, please try requesting the code again.") : ''}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <RadioGroup
                  row
                  value={otpMethod}
				  onChange={(e) => {
				    setOtpMethod(e.target.value);
				    setOtpSent(false); // Reset otpSent to false when OTP method changes
				  }}
                >
                  <FormControlLabel
                    value="email"
                    control={<Radio />}
                    label={state?.email}
                  />
                  <FormControlLabel
                    value="phone"
                    control={<Radio />}
                    label={state?.phone}
                  />
                </RadioGroup>
              </Grid>

			  {otpSent && (
              <Grid item xs={12}>
                <MuiOtpInput autoFocus length={6} value={otp} onChange={handleOtpChange} onComplete={validateOtp} />
              </Grid>
              )}

              {errorMessage && (
                <Grid item xs={12}>
                  <Alert severity="error">{errorMessage}</Alert>
                </Grid>
              )}

              {!otpSent && (
                <Grid item xs={12}>
                  <Button fullWidth variant="contained" color="primary" onClick={getCode}>
                    {intl.get("getcode").d("Get code")}
                  </Button>
                </Grid>
              )}

              {otpSent && (
                <>
                  <Grid item xs={12} container justifyContent="center">
                    <Typography component="p" variant="caption" sx={{ color: "secondary.dark" }}>
                      {intl.get("didntgetthecode").d("Didn't get the code?")}
                      &nbsp;
                      <Link component="button" variant="body2" onClick={resendOtp} sx={{ cursor: 'pointer' }}>
                        {intl.get("clickheretoresend").d("Click here to resend.")}
                      </Link>
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Form>
        </Box>
      </Box>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}